import React, { useState, useEffect, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { GetSettings, UpdateSettings } from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Modal de Configuración General y Reglas Operativas para la dinámica "Arma la Playlist" (Tile ID 46).
 * Permite ajustar cooldown, filtro de groserías, cantidad de QRs en cintillo, modo de pantalla,
 * visibilidad de créditos y títulos de visualización en estadio.
 * 
 * @component PlaylistSettingsModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Visibilidad del modal.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onSuccess] - Callback al guardar con éxito.
 */
export const PlaylistSettingsModal = ({ isOpen, onClose, onSuccess }) => {
    const { showMessage } = useFloatMessage();

    // Form state
    const [formData, setFormData] = useState({
        cooldownSeconds: 120,
        isSubmissionActive: true,
        submissionPausedMessage: '¡Gracias por participar! La playlist para este partido ha cerrado.',
        isProfanityFilterEnabled: false,
        qrCount: 1, // 0: Simple (1 QR), 1: Double (2 QRs)
        screenMode: 0, // 0: CallToAction, 1: NowPlaying, 2: Queue
        isScreenQrVisible: true,
        showFanNameOnScreen: true,
        showVoteCountOnScreen: true,
        screenTitle: 'ARMA LA PLAYLIST',
        screenSubtitle: '¿QUÉ CANCIÓN O ARTISTA FALTA EN LA PLAYLIST?',
        overlayOffsetY: 0,
        isOverlayVisible: true,
        isOverlayNowPlayingVisible: false,
        qrUrl: '',
        qrCenterLogoUrl: '',
        overlayBackground1536Url: '',
        overlayBackground1920Url: '',
        screenBackgroundUrl: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Cargar configuración existente al abrir
    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await GetSettings();
            if (res && res.success && res.data) {
                setFormData(prev => ({
                    ...prev,
                    ...res.data,
                    cooldownSeconds: res.data.cooldownSeconds ?? 120,
                    isSubmissionActive: res.data.isSubmissionActive ?? true,
                    submissionPausedMessage: res.data.submissionPausedMessage || '¡Gracias por participar! La playlist para este partido ha cerrado.',
                    isProfanityFilterEnabled: !!res.data.isProfanityFilterEnabled,
                    qrCount: Number(res.data.qrCount ?? 1),
                    screenMode: Number(res.data.screenMode ?? 0),
                    isScreenQrVisible: res.data.isScreenQrVisible ?? true,
                    showFanNameOnScreen: res.data.showFanNameOnScreen ?? true,
                    showVoteCountOnScreen: res.data.showVoteCountOnScreen ?? true,
                    screenTitle: res.data.screenTitle || 'ARMA LA PLAYLIST',
                    screenSubtitle: res.data.screenSubtitle || '¿QUÉ CANCIÓN O ARTISTA FALTA EN LA PLAYLIST?',
                    overlayOffsetY: res.data.overlayOffsetY ?? 0
                }));
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al consultar la configuración actual.',
                message: 'Error al consultar la configuración actual.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        if (isOpen) {
            fetchSettings();
        }
    }, [isOpen, fetchSettings]);

    // Cerrar con Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape' && isOpen) {
            onClose();
        }
    }, [isOpen, onClose]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    if (!isOpen) return null;

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.cooldownSeconds < 0) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El cooldown no puede ser un número negativo.',
                message: 'El cooldown no puede ser un número negativo.'
            });
            return;
        }

        if (!formData.screenTitle.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El título de pantalla no puede estar vacío.',
                message: 'El título de pantalla no puede estar vacío.'
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                cooldownSeconds: Number(formData.cooldownSeconds),
                qrCount: Number(formData.qrCount),
                screenMode: Number(formData.screenMode),
                overlayOffsetY: Number(formData.overlayOffsetY || 0),
                screenTitle: formData.screenTitle.trim(),
                screenSubtitle: formData.screenSubtitle.trim(),
                submissionPausedMessage: formData.submissionPausedMessage.trim()
            };

            const res = await UpdateSettings(payload);

            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '⚙️ Configuración de Playlist guardada exitosamente.',
                    message: '⚙️ Configuración de Playlist guardada exitosamente.'
                });

                if (onSuccess && typeof onSuccess === 'function') {
                    onSuccess(res.data || payload);
                }
                onClose();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al guardar la configuración.',
                    message: (res && res.message) || 'Error al guardar la configuración.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar la configuración.',
                message: 'Error de red al actualizar la configuración.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="PlaylistModal__Overlay" role="dialog" aria-modal="true">
            <div className="PlaylistModal__Backdrop" onClick={onClose} />
            <div className="PlaylistModal__Dialog PlaylistModal__Dialog--large">
                <div className="PlaylistModal__Content">
                    {/* Header */}
                    <div className="PlaylistModal__Header">
                        <div className="d-flex align-items-center gap-2">
                            <span className="PlaylistModal__HeaderIcon PlaylistModal__HeaderIcon--settings">
                                <i className="bi bi-gear-fill text-primary"></i>
                            </span>
                            <div>
                                <h4 className="PlaylistModal__Title mb-0">Configuración de Playlist</h4>
                                <p className="PlaylistModal__Subtitle mb-0">
                                    Ajustes operativos, filtros, pantalla gigante y comportamiento en vivo
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="PlaylistModal__CloseButton"
                            onClick={onClose}
                            aria-label="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Body Form */}
                    <form onSubmit={handleSubmit}>
                        <div className="PlaylistModal__Body PlaylistModal__Body--scrollable">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status"></div>
                                    <p className="text-white-50 mt-2 small">Cargando configuración actual...</p>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {/* SECCIÓN 1: REGLAS DE PARTICIPACIÓN */}
                                    <div className="col-12">
                                        <div className="PlaylistModal__SectionTitle">
                                            <i className="bi bi-sliders me-2 text-danger"></i>
                                            Reglas de Participación de Aficionados
                                        </div>
                                    </div>

                                    {/* Cooldown en Segundos */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label" htmlFor="settings-cooldown">
                                                Cooldown entre Sugerencias (Segundos)
                                            </label>
                                            <div className="PlaylistModal__InputWrapper">
                                                <i className="bi bi-stopwatch PlaylistModal__InputIcon"></i>
                                                <input
                                                    id="settings-cooldown"
                                                    type="number"
                                                    min="0"
                                                    max="3600"
                                                    className="PlaylistModal__Input"
                                                    value={formData.cooldownSeconds}
                                                    onChange={(e) => handleChange('cooldownSeconds', Math.max(0, parseInt(e.target.value, 10) || 0))}
                                                    required
                                                />
                                            </div>
                                            <span className="PlaylistModal__HelpText">
                                                Tiempo mínimo que debe esperar un aficionado antes de enviar otra sugerencia (por defecto: 120s).
                                            </span>
                                        </div>
                                    </div>

                                    {/* Filtro de Palabras Inapropiadas */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label">
                                                Filtro de Palabras Inapropiadas
                                            </label>
                                            <div className="PlaylistModal__SwitchBox">
                                                <label className="PlaylistModal__Switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.isProfanityFilterEnabled}
                                                        onChange={(e) => handleChange('isProfanityFilterEnabled', e.target.checked)}
                                                    />
                                                    <span className="PlaylistModal__Slider"></span>
                                                </label>
                                                <span className="PlaylistModal__SwitchLabel">
                                                    {formData.isProfanityFilterEnabled
                                                        ? '🛡️ Filtro ACTIVO (Censura o rechaza obscenidades)'
                                                        : '⚠️ Filtro DESACTIVADO (Permitir texto libre)'}
                                                </span>
                                            </div>
                                            <span className="PlaylistModal__HelpText">
                                                Detecta y oculta automáticamente lenguaje ofensivo o insultos enviados por aficionados.
                                            </span>
                                        </div>
                                    </div>

                                    {/* SECCIÓN 2: PANTALLA GIGANTE Y CINTILLOS */}
                                    <div className="col-12 pt-2">
                                        <div className="PlaylistModal__SectionTitle">
                                            <i className="bi bi-display me-2 text-danger"></i>
                                            Proyección en Pantalla Gigante y Cintillo
                                        </div>
                                    </div>

                                    {/* Selector de QRs en Cintillo */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label" htmlFor="settings-qrcount">
                                                Código QR en Cintillo Overlay
                                            </label>
                                            <div className="PlaylistModal__InputWrapper">
                                                <i className="bi bi-qr-code PlaylistModal__InputIcon"></i>
                                                <select
                                                    id="settings-qrcount"
                                                    className="PlaylistModal__Input PlaylistModal__Select"
                                                    value={formData.qrCount}
                                                    onChange={(e) => handleChange('qrCount', parseInt(e.target.value, 10))}
                                                >
                                                    <option value={0}>1 QR (Simple - Lado Izquierdo)</option>
                                                    <option value={1}>2 QRs (Doble - Ambos Extremos)</option>
                                                </select>
                                            </div>
                                            <span className="PlaylistModal__HelpText">
                                                Configura si el cintillo de televisión muestra uno o dos códigos QR de interacción.
                                            </span>
                                        </div>
                                    </div>

                                    {/* Switch Mostrar Créditos de Aficionado */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label">
                                                Créditos del Aficionado en Pantalla
                                            </label>
                                            <div className="PlaylistModal__SwitchBox">
                                                <label className="PlaylistModal__Switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.showFanNameOnScreen}
                                                        onChange={(e) => handleChange('showFanNameOnScreen', e.target.checked)}
                                                    />
                                                    <span className="PlaylistModal__Slider"></span>
                                                </label>
                                                <span className="PlaylistModal__SwitchLabel">
                                                    {formData.showFanNameOnScreen
                                                        ? '👤 Mostrar "Pedida por [Nombre]"'
                                                        : '🚫 Ocultar nombre del aficionado'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Switch Mostrar Contador de Votos en Pantalla */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label">
                                                Contador de Votos en Pantalla
                                            </label>
                                            <div className="PlaylistModal__SwitchBox">
                                                <label className="PlaylistModal__Switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.showVoteCountOnScreen}
                                                        onChange={(e) => handleChange('showVoteCountOnScreen', e.target.checked)}
                                                    />
                                                    <span className="PlaylistModal__Slider"></span>
                                                </label>
                                                <span className="PlaylistModal__SwitchLabel">
                                                    {formData.showVoteCountOnScreen
                                                        ? '🔥 Mostrar número de votos en pantalla'
                                                        : '🚫 Ocultar conteo de votos'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SECCIÓN 3: TEXTOS Y MENSAJES */}
                                    <div className="col-12 pt-2">
                                        <div className="PlaylistModal__SectionTitle">
                                            <i className="bi bi-chat-square-text me-2 text-danger"></i>
                                            Títulos y Mensajes Públicos
                                        </div>
                                    </div>

                                    {/* Título de Pantalla */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label" htmlFor="settings-screen-title">
                                                Título Principal en Pantalla <span className="text-danger">*</span>
                                            </label>
                                            <div className="PlaylistModal__InputWrapper">
                                                <i className="bi bi-fonts PlaylistModal__InputIcon"></i>
                                                <input
                                                    id="settings-screen-title"
                                                    type="text"
                                                    className="PlaylistModal__Input"
                                                    value={formData.screenTitle}
                                                    onChange={(e) => handleChange('screenTitle', e.target.value)}
                                                    maxLength={200}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Subtítulo de Pantalla */}
                                    <div className="col-12 col-md-6">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label" htmlFor="settings-screen-subtitle">
                                                Subtítulo / Instrucción en Pantalla <span className="text-danger">*</span>
                                            </label>
                                            <div className="PlaylistModal__InputWrapper">
                                                <i className="bi bi-type PlaylistModal__InputIcon"></i>
                                                <input
                                                    id="settings-screen-subtitle"
                                                    type="text"
                                                    className="PlaylistModal__Input"
                                                    value={formData.screenSubtitle}
                                                    onChange={(e) => handleChange('screenSubtitle', e.target.value)}
                                                    maxLength={300}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Mensaje de Pausa */}
                                    <div className="col-12">
                                        <div className="PlaylistModal__Field">
                                            <label className="PlaylistModal__Label" htmlFor="settings-pause-message">
                                                Mensaje de Recepción Pausada / Cerrada
                                            </label>
                                            <div className="PlaylistModal__InputWrapper">
                                                <i className="bi bi-pause-btn PlaylistModal__InputIcon"></i>
                                                <input
                                                    id="settings-pause-message"
                                                    type="text"
                                                    className="PlaylistModal__Input"
                                                    value={formData.submissionPausedMessage}
                                                    onChange={(e) => handleChange('submissionPausedMessage', e.target.value)}
                                                    maxLength={500}
                                                    placeholder="¡Gracias por participar! La playlist para este partido ha cerrado."
                                                />
                                            </div>
                                            <span className="PlaylistModal__HelpText">
                                                Texto que visualizarán los aficionados en el móvil cuando el operador pause la recepción.
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="PlaylistModal__Footer">
                            <button
                                type="button"
                                className="PlaylistModal__Btn PlaylistModal__Btn--secondary"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="PlaylistModal__Btn PlaylistModal__Btn--primary"
                                disabled={isSaving || isLoading}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check2-circle me-2"></i>
                                        Guardar Configuración
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PlaylistSettingsModal;
